import { ArrowBack, Close as CloseIcon } from '@mui/icons-material';
import {
    Box,
    Button,
    Skeleton,
    Tab,
    Tabs,
    Typography,
    alpha,
} from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';

import { theme } from 'app/providers/ThemeProvider/config/theme';
import { DisplayItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { EditMinAllowedInStockInput } from 'features/EditMinAllowedInStock';
import { MoveToInventoryCatalogItemButton } from 'features/moveToInventoryCatalogItem';
import { MoveToInventoryDialog } from 'features/moveToInventoryCatalogItem/ui/MoveToInventoryDialog/MoveToInventoryDialog';
import { RemoveCatalogItem } from 'features/removeCatalogItem';
import { getPrice } from 'shared/lib/getPrice/getPrice';
import {
    getTermFromCode,
    shouldShowUOM,
} from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { Loader } from 'shared/ui/Loader/Loader';
import { MarkdownDisplay } from 'shared/ui/MarkdownDisplay/MarkdownDisplay';
import { SuppliersList } from 'shared/ui/SuppliersList';
import {
    decodeHtmlEntities,
    formatCommaList,
} from 'shared/utils/textFormatters/textFormatters';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { usePropertyCatalogItemDetailedHooks } from './PropertyCatalogItemDetailedHooks';
import { PropertyCatalogItemRow } from './PropertyCatalogItemRow';

export interface CatalogItemProps {
    displayItem: DisplayItem;
    id: string;
    price: number;
    isOrderPage: boolean;
    minAllowedInStock: number;
    moveTo?: PropertyCatalogItemsMoveTo;
    isLocal: boolean;
    onCancel: () => void;
    /** When true, suppresses all Min On Hand UI (value row + edit control). */
    hideMinOnHand?: boolean;
    /**
     * Controls the header layout.
     * - 'catalog' (default): back button + separate name heading
     * - 'close-only': name + close icon in a single row (used in Savings Summary drawer)
     */
    headerMode?: 'catalog' | 'close-only';
}

interface ItemMetadataField {
    label: string;
    value: string | number;
    shouldRender?: boolean;
}

export const PropertyCatalogItemDetailed = memo(
    ({
        displayItem,
        price,
        isOrderPage,
        id,
        minAllowedInStock,
        moveTo,
        isLocal,
        onCancel,
        hideMinOnHand = false,
        headerMode = 'catalog',
    }: CatalogItemProps) => {
        const scrollContainerRef = useRef<HTMLDivElement>(null);

        const {
            t,
            backButtonText,
            hydratedSpecifications,
            showActionsPanel,
            canEditCatalog,
            canSeePrices,
            initialMinAllowedInStock,
            valueChanged,
            isGlobalCatalog,
            showEditOnHand,
            loading,
            proposalsLoading,
            proposalIsActive,
            goBack,
            onSubmit,
            activeTab,
            handleTabChange,
            handleOpenLightbox,
        } = usePropertyCatalogItemDetailedHooks({
            id,
            displayItem,
            isOrderPage,
            minAllowedInStock,
            moveTo,
            isLocal,
            onCancel,
            scrollContainerRef,
        });

        const effectiveShowEditOnHand = !hideMinOnHand && showEditOnHand;

        const metadataFields: ItemMetadataField[] = useMemo(
            () => [
                {
                    label:
                        t('Category') +
                        (displayItem.glCode ? t(' | GL Code') : ''),
                    value: displayItem.category,
                    shouldRender: !!displayItem.category,
                },
                {
                    label: t('Brand'),
                    value: displayItem.brand ?? '',
                    shouldRender: !!displayItem.brand,
                },
                {
                    label: t('RE-OPS SKU'),
                    value: displayItem.sku,
                    shouldRender: !!displayItem.sku,
                },
                {
                    label: t('Manufacturer part'),
                    value: displayItem.mpn ?? '',
                    shouldRender: !!displayItem.mpn,
                },
                {
                    label: t('Unit of measure'),
                    value: getTermFromCode(displayItem.uom) ?? '',
                    shouldRender: shouldShowUOM(displayItem.uom),
                },
                {
                    label: t('Quantity per UOM'),
                    value: displayItem?.qtyPerUom ?? 0,
                    shouldRender: shouldShowUOM(displayItem.uom),
                },
                {
                    label: t('Price per UOM'),
                    value: getPrice(displayItem.uomPrice || price),
                    shouldRender:
                        shouldShowUOM(displayItem.uom) && canSeePrices,
                },
                {
                    label: t('Price'),
                    value: getPrice(displayItem.uomPrice || price),
                    shouldRender: canSeePrices,
                },
                {
                    label: t('Min On Hand'),
                    value: minAllowedInStock ?? 0,
                    shouldRender:
                        !hideMinOnHand &&
                        (isOrderPage || !effectiveShowEditOnHand) &&
                        !isGlobalCatalog &&
                        minAllowedInStock > 0,
                },
            ],
            [
                t,
                displayItem,
                minAllowedInStock,
                canSeePrices,
                price,
                isOrderPage,
                effectiveShowEditOnHand,
                isGlobalCatalog,
                hideMinOnHand,
            ],
        );

        const specificationsKeys = useMemo(
            () =>
                hydratedSpecifications
                    ? Object.keys(hydratedSpecifications).sort((a, b) =>
                          a.localeCompare(b),
                      )
                    : [],
            [hydratedSpecifications],
        );

        const specIsProp65 = useCallback((key: string) => {
            return key === 'Proposition 65';
        }, []);

        return (
            <Box
                width="100%"
                height="100%"
                display="flex"
                flexDirection="column"
            >
                {/* ----- Header ----- */}
                <Box
                    component="header"
                    display="flex"
                    justifyContent="space-between"
                    marginBottom="20px"
                    flex="1 1"
                    flexGrow="0"
                    paddingRight="16px"
                    alignItems="center"
                >
                    {headerMode === 'catalog' ? (
                        <Button
                            onClick={goBack}
                            title={`Back to ${backButtonText}`}
                            sx={(theme) => ({
                                color: alpha(theme.palette.common.black, 0.5),
                                p: '0',
                                minWidth: '24px',
                            })}
                        >
                            <ArrowBack
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    marginRight: '2px',
                                }}
                            />
                            <Box
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '200px',
                                }}
                            >
                                {backButtonText}
                            </Box>
                        </Button>
                    ) : (
                        <Typography
                            data-testid="property-catalog-item-detailed-name"
                            sx={{
                                color: alpha(theme.palette.common.black, 0.85),
                                fontFamily: 'Poppins',
                                fontSize: '22px',
                                fontWeight: 600,
                                lineHeight: '32px',
                            }}
                        >
                            {decodeHtmlEntities(displayItem.name)}
                        </Typography>
                    )}
                    <Box display="flex" gap="24px" alignItems="center">
                        {showActionsPanel && canEditCatalog && (
                            <Box display="flex" gap="24px">
                                <RemoveCatalogItem isSingleItem />
                                <MoveToInventoryCatalogItemButton
                                    isSingleItem
                                />
                            </Box>
                        )}
                        {headerMode === 'close-only' && (
                            <Button
                                onClick={onCancel}
                                sx={{
                                    minWidth: 'auto',
                                    padding: 0,
                                    color: alpha(
                                        theme.palette.common.black,
                                        0.6,
                                    ),
                                }}
                                aria-label="Close"
                            >
                                <CloseIcon />
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* ----- Name (when in catalog mode) and Tabs ----- */}
                {headerMode === 'catalog' && (
                    <Typography
                        data-testid="property-catalog-item-detailed-name"
                        sx={{
                            color: alpha(theme.palette.common.black, 0.85),
                            fontFamily: 'Poppins',
                            fontSize: '22px',
                            fontWeight: 600,
                            lineHeight: '32px',
                        }}
                    >
                        {decodeHtmlEntities(displayItem.name)}
                    </Typography>
                )}
                <Box
                    sx={{
                        marginBottom: '16px',
                        marginTop: '16px',
                        paddingRight: '16px',
                    }}
                >
                    <Tabs
                        data-testid="property-catalog-item-detailed-tabs"
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            position: 'relative',
                            minHeight: '42px',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '1px',
                                backgroundColor: theme.palette.divider,
                                zIndex: 0,
                            },
                            '& .MuiTab-root': {
                                fontSize: '14px',
                                lineHeight: '24px',
                                letterSpacing: '0.1px',
                                textTransform: 'none',
                                minHeight: '42px',
                                padding: '9px 16px',
                                color: alpha(theme.palette.common.black, 0.6),
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                },
                            },
                            '& .MuiTabs-indicator': {
                                height: '2px',
                                backgroundColor: theme.palette.primary.main,
                                bottom: 0,
                                zIndex: 1,
                            },
                        }}
                    >
                        <Tab label={t('Details')} value="details" />
                        <Tab label={t('Suppliers')} value="suppliers" />
                        {displayItem.specifications &&
                            Object.keys(displayItem.specifications).length >
                                0 && (
                                <Tab
                                    label={t('Specifications')}
                                    value="specifications"
                                />
                            )}
                    </Tabs>
                </Box>
                {/* ----- Content ----- */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        width: '100%',
                        overflowY: 'auto',
                        paddingRight: '16px',
                        paddingBottom: '40px',
                    }}
                >
                    <Box id="section-details">
                        <Box
                            sx={{
                                borderRadius: '4px',
                                display: 'flex',
                                height: 'auto',
                                minHeight: '200px',
                                maxHeight: '320px',
                                width: '100%',
                                cursor: 'pointer',
                                margin: '30px 0',
                            }}
                            onClick={handleOpenLightbox}
                        >
                            <CatalogItemImage
                                src={displayItem.imageUrl}
                                isFull={true}
                            />
                        </Box>

                        <Box>
                            {metadataFields.map(
                                (field) =>
                                    field.shouldRender && (
                                        <PropertyCatalogItemRow
                                            bordered
                                            key={field.label}
                                            name={field.label}
                                            value={decodeHtmlEntities(
                                                field.value?.toString() ?? '',
                                            )}
                                        />
                                    ),
                            )}
                        </Box>

                        {displayItem.description && (
                            <Box
                                sx={{
                                    marginTop: '16px',
                                    marginBottom: '16px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: alpha(
                                            theme.palette.common.black,
                                            0.5,
                                        ),
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '20px',
                                        letterSpacing: '0.15px',
                                        marginBottom: '16px',
                                    }}
                                >
                                    {t('Description')}
                                </Typography>
                                {displayItem.descriptionFormat ===
                                'markdown' ? (
                                    <MarkdownDisplay
                                        markdown={displayItem.description}
                                    />
                                ) : (
                                    <Typography
                                        sx={{
                                            color: alpha(
                                                theme.palette.common.black,
                                                0.85,
                                            ),
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {displayItem.description}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {effectiveShowEditOnHand && (
                            <Box m="24px 0 48px">
                                <EditMinAllowedInStockInput
                                    readOnly={!canEditCatalog}
                                    initialValue={initialMinAllowedInStock}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Suppliers */}
                    <Box
                        data-testid="property-catalog-item-detailed-suppliers"
                        id="section-suppliers"
                        sx={{ flex: 1, marginTop: '46px' }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            marginBottom="8px"
                        >
                            <Typography
                                sx={{
                                    color: alpha(
                                        theme.palette.common.black,
                                        0.85,
                                    ),
                                    fontFamily: 'Poppins',
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    lineHeight: '32px',
                                }}
                            >
                                {t('Suppliers')}
                            </Typography>
                            {displayItem.proposals?.length > 0 && (
                                <Typography
                                    sx={{
                                        color: alpha(
                                            theme.palette.common.black,
                                            0.85,
                                        ),
                                        fontFamily: 'Open Sans',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        lineHeight: '32px',
                                        letterSpacing: '0.2px',
                                    }}
                                >
                                    {t('SKU')}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            {proposalsLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '16px 0',
                                    }}
                                >
                                    <Skeleton
                                        variant="rounded"
                                        height="24px"
                                        width="100%"
                                    />
                                </Box>
                            ) : displayItem.proposals &&
                              displayItem.proposals.length > 0 ? (
                                <SuppliersList
                                    proposals={displayItem.proposals}
                                    showSku
                                    bordered
                                />
                            ) : (
                                <Typography
                                    sx={{
                                        color: alpha(
                                            theme.palette.common.black,
                                            0.5,
                                        ),
                                        fontFamily: 'Open Sans',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '20px',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    {t(
                                        'This item is currently not offered by any of your suppliers.',
                                    )}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* ----- Specifications ----- */}
                    {specificationsKeys.length > 0 && (
                        <Box
                            data-testid="property-catalog-item-detailed-specifications"
                            id="section-specifications"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginTop: '40px',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: alpha(
                                        theme.palette.common.black,
                                        0.85,
                                    ),
                                    fontFamily: 'Poppins',
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    lineHeight: '32px',
                                    marginBottom: '8px',
                                }}
                            >
                                {t('Specifications')}
                            </Typography>
                            {specificationsKeys.map((specificationKey) => (
                                <PropertyCatalogItemRow
                                    bordered
                                    key={specificationKey}
                                    name={decodeHtmlEntities(specificationKey)}
                                    value={formatCommaList(
                                        hydratedSpecifications[
                                            specificationKey
                                        ],
                                    )}
                                    showWarningIcon={specIsProp65(
                                        specificationKey,
                                    )}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {isLocal && !isOrderPage && canEditCatalog && valueChanged && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            paddingTop: '16px',
                            paddingRight: '16px',
                        }}
                    >
                        <Button onClick={goBack}>{t('Cancel')}</Button>
                        {canEditCatalog && (
                            <Button
                                disabled={!valueChanged || loading}
                                startIcon={
                                    loading && <Loader color="inherit" />
                                }
                                variant="contained"
                                onClick={onSubmit}
                            >
                                {t('Save')}
                            </Button>
                        )}
                    </Box>
                )}

                <MoveToInventoryDialog />
            </Box>
        );
    },
);
